import React, { useEffect, useState } from 'react';
import { adminAPI } from '../../../api';
// eslint-disable-next-line @typescript-eslint/no-var-requires
const XLSX = require('xlsx');

const AdminRestBox: React.FC = () => {
  const [boxes, setBoxes] = useState<string[]>(Array(50).fill(''));
  const [rotation, setRotation] = useState({
    enabled: false, interval: 300, count: 2,
    triggerType: 'time', questionCount: 20,
    mockType: 'time', mockQuestionCount: 20,
  });
  const [activeTab, setActiveTab] = useState<'restbox' | 'video'>('restbox');
  const [xlsxUploading, setXlsxUploading] = useState(false);
  const [xlsxResult, setXlsxResult] = useState('');
  const [savingRotation, setSavingRotation] = useState(false);

  const [videoSettings, setVideoSettings] = useState({
    pilgi: { enabled: false, type: 'time', timeInterval: 600, questionInterval: 20, minWatch: 0 },
    silgi: { enabled: false, type: 'time', timeInterval: 600, questionInterval: 20, minWatch: 0 },
    videoTab: 'pilgi' as 'pilgi' | 'silgi',
    urls: Array(10).fill(''),
  });

  useEffect(() => {
    // 휴식박스 로드
    adminAPI.restBoxes().then(res => {
      const arr = Array(50).fill('');
      res.data.forEach((b: any, i: number) => { if (i < 50) arr[i] = b.content; });
      setBoxes(arr);
    });

    // 로테이션 설정 로드 (SiteSettings)
    adminAPI.settings().then(res => {
      const s = res.data;
      setRotation({
        enabled: s.rest_rotation_enabled ?? false,
        count: s.rest_rotation_count ?? 2,
        triggerType: s.rest_normal_type ?? 'time',
        interval: s.rest_normal_interval ?? 300,
        questionCount: s.rest_normal_question ?? 20,
        mockType: s.rest_mock_type ?? 'time',
        mockQuestionCount: s.rest_mock_question ?? 20,
      });
    });

    // 동영상 팝업 로드
    adminAPI.videoPopups().then(res => {
      res.data.forEach((v: any) => {
        if (v.exam_type === '필기') {
          const urls = v.video_urls ? v.video_urls.split('\n') : [];
          const arr = Array(10).fill('');
          urls.forEach((u: string, i: number) => { if (i < 10) arr[i] = u; });
          setVideoSettings(prev => ({
            ...prev,
            pilgi: { enabled: v.is_active, type: v.trigger_type, timeInterval: v.time_interval, questionInterval: v.question_interval, minWatch: v.min_watch_time },
            urls: arr,
          }));
        }
        if (v.exam_type === '실기') {
          setVideoSettings(prev => ({
            ...prev,
            silgi: { enabled: v.is_active, type: v.trigger_type, timeInterval: v.time_interval, questionInterval: v.question_interval, minWatch: v.min_watch_time },
          }));
        }
      });
    });
  }, []);

  // 로테이션 설정 저장
  const saveRotation = async () => {
    setSavingRotation(true);
    try {
      await adminAPI.updateSettings({
        rest_rotation_enabled: rotation.enabled,
        rest_rotation_count: rotation.count,
        rest_normal_type: rotation.triggerType,
        rest_normal_interval: rotation.interval,
        rest_normal_question: rotation.questionCount,
        rest_mock_type: rotation.mockType,
        rest_mock_interval: rotation.interval,
        rest_mock_question: rotation.mockQuestionCount,
      });
      alert('로테이션 설정이 저장되었습니다.');
    } catch (e) { alert('저장 실패'); }
    setSavingRotation(false);
  };

  const saveRestBox = async () => {
    try {
      await adminAPI.updateRestBoxes(boxes.filter(b => b.trim()));
      alert('휴식박스 저장되었습니다.');
    } catch (e) { alert('저장 실패'); }
  };

  // 엑셀 업로드 (B열=내용, C열=ONOFF)
  const handleXlsxUpload = async (e: React.ChangeEvent<HTMLInputElement>) => {
    const file = e.target.files?.[0];
    if (!file) return;
    setXlsxUploading(true);
    setXlsxResult('');

    try {
      const ab = await file.arrayBuffer();
      const wb = XLSX.read(ab, { type: 'array' });
      const ws = wb.Sheets[wb.SheetNames[0]];
      const rows: any[][] = XLSX.utils.sheet_to_json(ws, { header: 1 });

      const loaded: string[] = [];
      for (let i = 1; i < rows.length; i++) {
        const row = rows[i];
        if (!row) continue;
        const content = row[1];
        const onoff = row[2];
        if (content !== undefined && content !== null && String(content).trim() !== '') {
          if (onoff === 1 || onoff === '1' || Number(onoff) === 1) {
            loaded.push(String(content).trim());
          }
        }
      }

      if (loaded.length === 0) {
        setXlsxResult('❌ ONOFF가 1인 항목이 없습니다. C열을 확인해 주세요.');
        setXlsxUploading(false);
        return;
      }

      const newBoxes = Array(50).fill('');
      loaded.slice(0, 50).forEach((v, i) => { newBoxes[i] = v; });
      setBoxes(newBoxes);
      await adminAPI.updateRestBoxes(loaded.slice(0, 50));
      setXlsxResult(`✅ ${loaded.length}개 로드 → ${Math.min(loaded.length, 50)}개 저장 완료`);
    } catch (err: any) {
      setXlsxResult('❌ 엑셀 읽기 실패: ' + err.message);
    }

    setXlsxUploading(false);
    e.target.value = '';
  };

  const saveVideoSettings = async () => {
    try {
      const urls = videoSettings.urls.filter(u => u.trim()).join('\n');
      await adminAPI.updateVideoPopup('필기', {
        is_active: videoSettings.pilgi.enabled,
        trigger_type: videoSettings.pilgi.type,
        time_interval: videoSettings.pilgi.timeInterval,
        question_interval: videoSettings.pilgi.questionInterval,
        min_watch_time: videoSettings.pilgi.minWatch,
        video_urls: urls,
      });
      await adminAPI.updateVideoPopup('실기', {
        is_active: videoSettings.silgi.enabled,
        trigger_type: videoSettings.silgi.type,
        time_interval: videoSettings.silgi.timeInterval,
        question_interval: videoSettings.silgi.questionInterval,
        min_watch_time: videoSettings.silgi.minWatch,
        video_urls: urls,
      });
      alert('동영상 팝업 설정 저장되었습니다.');
    } catch (e) { alert('저장 실패'); }
  };

  const curVideo = videoSettings.videoTab === 'pilgi' ? videoSettings.pilgi : videoSettings.silgi;
  const setCurVideo = (data: any) => {
    if (videoSettings.videoTab === 'pilgi') {
      setVideoSettings(prev => ({ ...prev, pilgi: { ...prev.pilgi, ...data } }));
    } else {
      setVideoSettings(prev => ({ ...prev, silgi: { ...prev.silgi, ...data } }));
    }
  };

  return (
    <div className="admin-page">
      <div className="admin-page-header">
        <div className="admin-page-title"><span className="icon">🎨</span>휴식 박스 관리</div>
      </div>

      {/* 탭 */}
      <div style={{ display: 'flex', gap: 0, marginBottom: 16, background: 'white', borderRadius: 10, padding: 4, boxShadow: 'var(--shadow-sm)', border: '1px solid var(--border)', width: 'fit-content' }}>
        <button className={`btn ${activeTab === 'restbox' ? 'btn-primary' : 'btn-ghost'}`} style={{ borderRadius: 8 }} onClick={() => setActiveTab('restbox')}>🖼️ 이미지 로테이션</button>
        <button className={`btn ${activeTab === 'video' ? 'btn-primary' : 'btn-ghost'}`} style={{ borderRadius: 8 }} onClick={() => setActiveTab('video')}>🎬 동영상 팝업</button>
      </div>

      {/* ===== 이미지 로테이션 ===== */}
      {activeTab === 'restbox' && (
        <>
          {/* 로테이션 설정 */}
          <div className="card" style={{ marginBottom: 16 }}>
            <div className="card-header" style={{ display: 'flex', justifyContent: 'space-between', alignItems: 'center' }}>
              <span>🔄 로테이션 설정</span>
              <button className="btn btn-primary btn-sm" onClick={saveRotation} disabled={savingRotation}>
                {savingRotation ? '저장 중...' : '💾 저장'}
              </button>
            </div>
            <div className="card-body">
              <div style={{ display: 'flex', flexWrap: 'wrap', gap: 20, alignItems: 'center' }}>
                <label style={{ display: 'flex', alignItems: 'center', gap: 6, fontSize: 13, cursor: 'pointer', fontWeight: 600 }}>
                  <input type="checkbox" checked={rotation.enabled}
                    onChange={e => setRotation({...rotation, enabled: e.target.checked})}
                    style={{ width: 16, height: 16 }} />
                  로테이션 활성화
                </label>
                <label style={{ display: 'flex', alignItems: 'center', gap: 8, fontSize: 13 }}>
                  <span style={{ color: 'var(--text-muted)', fontWeight: 600 }}>칸수</span>
                  <input type="number" className="form-control" style={{ width: 70 }}
                    value={rotation.count} min={1} max={8}
                    onChange={e => setRotation({...rotation, count: +e.target.value})} />
                </label>
                <div style={{ width: 1, height: 28, background: 'var(--border)' }} />
                <div style={{ display: 'flex', alignItems: 'center', gap: 12, fontSize: 13 }}>
                  <span style={{ fontWeight: 600, color: 'var(--text-muted)' }}>일반</span>
                  {[{v:'time', l:'시간간격'}, {v:'question', l:'문제수'}].map(opt => (
                    <label key={opt.v} style={{ display: 'flex', alignItems: 'center', gap: 4, cursor: 'pointer' }}>
                      <input type="radio" name="triggerType" value={opt.v}
                        checked={rotation.triggerType === opt.v}
                        onChange={() => setRotation({...rotation, triggerType: opt.v})} />
                      {opt.l}
                    </label>
                  ))}
                  {rotation.triggerType === 'time' ? (
                    <label style={{ display: 'flex', alignItems: 'center', gap: 6 }}>
                      <input type="number" className="form-control" style={{ width: 80 }}
                        value={rotation.interval}
                        onChange={e => setRotation({...rotation, interval: +e.target.value})} />
                      <span style={{ color: 'var(--text-muted)', fontSize: 13 }}>초</span>
                    </label>
                  ) : (
                    <label style={{ display: 'flex', alignItems: 'center', gap: 6 }}>
                      <input type="number" className="form-control" style={{ width: 80 }}
                        value={rotation.questionCount} min={1}
                        onChange={e => setRotation({...rotation, questionCount: +e.target.value})} />
                      <span style={{ color: 'var(--text-muted)', fontSize: 13 }}>문제마다</span>
                    </label>
                  )}
                </div>
                <div style={{ width: 1, height: 28, background: 'var(--border)' }} />
                <div style={{ display: 'flex', alignItems: 'center', gap: 12, fontSize: 13 }}>
                  <span style={{ fontWeight: 600, color: 'var(--text-muted)' }}>모의고사</span>
                  {[{v:'time', l:'시간간격'}, {v:'question', l:'문제수'}].map(opt => (
                    <label key={opt.v} style={{ display: 'flex', alignItems: 'center', gap: 4, cursor: 'pointer' }}>
                      <input type="radio" name="mockType" value={opt.v}
                        checked={rotation.mockType === opt.v}
                        onChange={() => setRotation({...rotation, mockType: opt.v})} />
                      {opt.l}
                    </label>
                  ))}
                  {rotation.mockType === 'time' ? (
                    <label style={{ display: 'flex', alignItems: 'center', gap: 6 }}>
                      <input type="number" className="form-control" style={{ width: 80 }}
                        value={rotation.interval}
                        onChange={e => setRotation({...rotation, interval: +e.target.value})} />
                      <span style={{ color: 'var(--text-muted)', fontSize: 13 }}>초</span>
                    </label>
                  ) : (
                    <label style={{ display: 'flex', alignItems: 'center', gap: 6 }}>
                      <input type="number" className="form-control" style={{ width: 80 }}
                        value={rotation.mockQuestionCount} min={1}
                        onChange={e => setRotation({...rotation, mockQuestionCount: +e.target.value})} />
                      <span style={{ color: 'var(--text-muted)', fontSize: 13 }}>문제마다</span>
                    </label>
                  )}
                </div>
              </div>
            </div>
          </div>

          {/* 박스 8개 */}
          <div className="card">
            <div className="card-header" style={{ display: 'flex', justifyContent: 'space-between', alignItems: 'center' }}>
              <span>🖼️ 휴식 박스 콘텐츠 (최대 50개)</span>
              <div style={{ display: 'flex', gap: 8, alignItems: 'center' }}>
                <label className="btn btn-ghost btn-sm" style={{ cursor: xlsxUploading ? 'not-allowed' : 'pointer', margin: 0 }}>
                  {xlsxUploading ? '⏳ 로딩 중...' : '📂 엑셀 업로드'}
                  <input type="file" accept=".xlsx,.xls" style={{ display: 'none' }}
                    onChange={handleXlsxUpload} disabled={xlsxUploading} />
                </label>
                <button className="btn btn-primary btn-sm" onClick={saveRestBox}>💾 저장</button>
              </div>
            </div>
            <div className="card-body">
              {xlsxResult && (
                <div style={{
                  marginBottom: 12, padding: '8px 12px', borderRadius: 6,
                  fontSize: 13, fontWeight: 600,
                  background: xlsxResult.startsWith('✅') ? '#dcfce7' : '#fee2e2',
                  color: xlsxResult.startsWith('✅') ? '#15803d' : '#dc2626',
                }}>
                  {xlsxResult}
                </div>
              )}
              <p style={{ fontSize: 12, color: 'var(--text-muted)', marginBottom: 14 }}>
                이미지 URL, YouTube URL, 또는 텍스트 입력<br />
                <strong>이미지 크기 형식:</strong> <code>[5img:경로]</code> = 5% 크기, <code>[120img:경로]</code> = 120% 크기<br />
                <strong>엑셀 형식:</strong> B열=휴식내용, C열=ONOFF (1=활성, 0=비활성)
              </p>
              <div style={{ display: 'grid', gridTemplateColumns: '1fr 1fr 1fr', gap: 8 }}>
                {boxes.map((box, i) => (
                  <div key={i} style={{ display: 'flex', alignItems: 'center', gap: 10 }}>
                    <span style={{ fontSize: 12, fontWeight: 700, color: 'var(--text-muted)', minWidth: 48, background: 'var(--gray-100)', padding: '4px 8px', borderRadius: 6, textAlign: 'center' }}>
                      박스{i+1}
                    </span>
                    <input className="form-control" placeholder="URL, [5img:경로] 또는 텍스트" style={{ fontSize: 12 }} value={box}
                      onChange={e => {
                        const arr = [...boxes];
                        arr[i] = e.target.value;
                        setBoxes(arr);
                      }} />
                  </div>
                ))}
              </div>
            </div>
          </div>
        </>
      )}

      {/* ===== 동영상 팝업 ===== */}
      {activeTab === 'video' && (
        <>
          <div className="card" style={{ marginBottom: 16 }}>
            <div className="card-header" style={{ display: 'flex', justifyContent: 'space-between', alignItems: 'center' }}>
              <span>🎬 동영상 팝업 설정</span>
              <div style={{ display: 'flex', gap: 6 }}>
                {(['pilgi','silgi'] as const).map(t => (
                  <button key={t} className={`btn btn-sm ${videoSettings.videoTab === t ? 'btn-primary' : 'btn-ghost'}`}
                    onClick={() => setVideoSettings(prev => ({...prev, videoTab: t}))}>
                    {t === 'pilgi' ? '📝 필기' : '🔧 실기'}
                  </button>
                ))}
              </div>
            </div>
            <div className="card-body">
              <div style={{ display: 'flex', alignItems: 'center', gap: 20, flexWrap: 'wrap', marginBottom: 16 }}>
                <label style={{ display: 'flex', alignItems: 'center', gap: 6, fontSize: 13, cursor: 'pointer', fontWeight: 600 }}>
                  <input type="checkbox" checked={curVideo.enabled}
                    onChange={e => setCurVideo({ enabled: e.target.checked })}
                    style={{ width: 16, height: 16 }} />
                  {videoSettings.videoTab === 'pilgi' ? '필기' : '실기'} 동영상 팝업 활성화
                </label>
              </div>
              <div style={{ display: 'flex', alignItems: 'center', gap: 16, flexWrap: 'wrap', padding: '14px 16px', background: 'var(--gray-50)', borderRadius: 8 }}>
                <div style={{ display: 'flex', alignItems: 'center', gap: 10, fontSize: 13 }}>
                  <span style={{ fontWeight: 600, color: 'var(--text-muted)' }}>팝업 조건</span>
                  {[{v:'time',l:'시간간격'},{v:'question',l:'문제수'}].map(opt => (
                    <label key={opt.v} style={{ display: 'flex', alignItems: 'center', gap: 4, cursor: 'pointer' }}>
                      <input type="radio" name={`videoType_${videoSettings.videoTab}`}
                        value={opt.v} checked={curVideo.type === opt.v}
                        onChange={() => setCurVideo({ type: opt.v })} />
                      {opt.l}
                    </label>
                  ))}
                </div>
                <label style={{ display: 'flex', alignItems: 'center', gap: 8, fontSize: 13 }}>
                  <span style={{ color: 'var(--text-muted)', fontWeight: 600 }}>시간</span>
                  <input type="number" className="form-control" style={{ width: 80 }}
                    value={curVideo.timeInterval} onChange={e => setCurVideo({ timeInterval: +e.target.value })} />
                  <span style={{ color: 'var(--text-muted)' }}>초</span>
                </label>
                <label style={{ display: 'flex', alignItems: 'center', gap: 8, fontSize: 13 }}>
                  <span style={{ color: 'var(--text-muted)', fontWeight: 600 }}>문제수</span>
                  <input type="number" className="form-control" style={{ width: 80 }}
                    value={curVideo.questionInterval} onChange={e => setCurVideo({ questionInterval: +e.target.value })} />
                  <span style={{ color: 'var(--text-muted)' }}>문제마다</span>
                </label>
                <label style={{ display: 'flex', alignItems: 'center', gap: 8, fontSize: 13 }}>
                  <span style={{ color: 'var(--text-muted)', fontWeight: 600 }}>최소시청</span>
                  <input type="number" className="form-control" style={{ width: 70 }}
                    value={curVideo.minWatch} onChange={e => setCurVideo({ minWatch: +e.target.value })} />
                  <span style={{ color: 'var(--text-muted)' }}>초</span>
                </label>
              </div>
            </div>
          </div>

          <div className="card">
            <div className="card-header" style={{ display: 'flex', justifyContent: 'space-between', alignItems: 'center' }}>
              <span>🎥 동영상 URL (최대 10개, 필기/실기 공통)</span>
              <button className="btn btn-primary btn-sm" onClick={saveVideoSettings}>💾 저장</button>
            </div>
            <div className="card-body">
              <p style={{ fontSize: 12, color: 'var(--text-muted)', marginBottom: 14 }}>순서대로 재생되며 마지막 후 첫 번째로 돌아갑니다.</p>
              <div style={{ display: 'grid', gridTemplateColumns: '1fr 1fr', gap: 8 }}>
                {videoSettings.urls.map((url, i) => (
                  <div key={i} style={{ display: 'flex', alignItems: 'center', gap: 8 }}>
                    <span style={{ fontSize: 12, fontWeight: 700, color: 'var(--text-muted)', minWidth: 28, background: 'var(--gray-100)', padding: '4px 6px', borderRadius: 6, textAlign: 'center' }}>{i+1}</span>
                    <input className="form-control" placeholder="YouTube URL (https://youtu.be/...)" value={url}
                      onChange={e => {
                        const arr = [...videoSettings.urls];
                        arr[i] = e.target.value;
                        setVideoSettings(prev => ({...prev, urls: arr}));
                      }} />
                  </div>
                ))}
              </div>
            </div>
          </div>
        </>
      )}
    </div>
  );
};

export default AdminRestBox;
